import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import "../styles/dailyReport.css";

function WeekLimits({ appState }) {
  const [sessionTypes, setSessionTypes] = useState([]);
  const [rowEdits, setRowEdits] = useState({});
  const [state, setState] = useState({
    status: "idle",
    message: "",
  });

  const siteId = appState.nav?.siteId;
  const locationId = appState.nav?.locationId;
  const token = appState.singleAuth?.token;

  const getSessionTypes = async () => {
    if (!siteId || !token) {
      setSessionTypes([]);
      setState({
        status: "error",
        message: "Missing site or authorization token.",
      });
      return;
    }

    setState((prev) => ({
      ...prev,
      status: "loading",
      message: "",
    }));

    try {
      const getResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/sessionTypes`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            siteid: siteId,
            locationid: locationId,
            authorization: token,
          },
        }
      );

      if (!getResponse.ok) {
        const errorData = await getResponse.json().catch(() => ({}));
        setSessionTypes([]);
        setState({
          status: "error",
          message: errorData.message || "Unable to load session types.",
        });
        return;
      }

      const responseData = await getResponse.json();
      const services = (responseData.services ?? []).filter((s) => s.seeOnLine === true);
      setSessionTypes(services);

      const editMap = {};
      services.forEach((service) => {
        editMap[service.sessionTypeId] = {
          start_week:
            service.start_week !== undefined && service.start_week !== null
              ? String(service.start_week)
              : "",
          end_week:
            service.end_week !== undefined && service.end_week !== null
              ? String(service.end_week)
              : "",
          saving: false,
        };
      });
      setRowEdits(editMap);

      setState({
        status: "success",
        message: "Session types loaded successfully.",
      });
    } catch (error) {
      setSessionTypes([]);
      setState({
        status: "error",
        message: error.message,
      });
    }
  };

  useEffect(() => {
    getSessionTypes();
  }, [siteId, token]);

  const handleWeekChange = (sessionTypeId, field, value) => {
    setRowEdits((prev) => ({
      ...prev,
      [sessionTypeId]: {
        ...(prev[sessionTypeId] || {}),
        [field]: value,
      },
    }));
  };

  const resetRow = (sessionTypeId) => {
    const current = sessionTypes.find(
      (item) => String(item.sessionTypeId) === String(sessionTypeId)
    );
    if (!current) return;

    setRowEdits((prev) => ({
      ...prev,
      [sessionTypeId]: {
        ...prev[sessionTypeId],
        start_week:
          current.start_week !== undefined && current.start_week !== null
            ? String(current.start_week)
            : "",
        end_week:
          current.end_week !== undefined && current.end_week !== null
            ? String(current.end_week)
            : "",
      },
    }));
  };

  const saveWeekFilter = async (sessionTypeId) => {
    const row = rowEdits[sessionTypeId] || {};
    const startWeek = Number(row.start_week);
    const endWeek = Number(row.end_week);

    if (!Number.isInteger(startWeek) || !Number.isInteger(endWeek)) {
      setState({
        status: "error",
        message: "start_week and end_week must be integer numbers.",
      });
      return;
    }

    if (startWeek > endWeek) {
      setState({
        status: "error",
        message: "start_week must be less than or equal to end_week.",
      });
      return;
    }

    setRowEdits((prev) => ({
      ...prev,
      [sessionTypeId]: {
        ...prev[sessionTypeId],
        saving: true,
      },
    }));

    const payload = {
      start_week: startWeek,
      end_week: endWeek,
    };

    try {
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/weekFilters/${sessionTypeId}`,
        {
          method: "PUT",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            siteid: siteId,
            locationid: locationId,
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.status === 404) {
        response = await fetch(`${process.env.REACT_APP_API_URL}/api/weekFilters`, {
          method: "POST",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            siteid: siteId,
            locationid: locationId,
          },
          body: JSON.stringify({
            Id: Number(sessionTypeId),
            ...payload,
          }),
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || "Failed to save row.");
      }

      setSessionTypes((prev) =>
        prev.map((item) =>
          String(item.sessionTypeId) === String(sessionTypeId)
            ? {
                ...item,
                start_week: startWeek,
                end_week: endWeek,
              }
            : item
        )
      );

      setState({
        status: "success",
        message: `Week limits updated for session type ${sessionTypeId}.`,
      });
    } catch (error) {
      setState({
        status: "error",
        message: error.message,
      });
    } finally {
      setRowEdits((prev) => ({
        ...prev,
        [sessionTypeId]: {
          ...prev[sessionTypeId],
          saving: false,
        },
      }));
    }
  };

  return (
    <div className="container" data-cy="cm-week-limits">
      <div className="my-5 d-flex justify-content-center">
        <h4 className="lb-title-daily">Week Limits</h4>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="lb-not-bold m-0">Session types with week filters</h5>
        <button className="lb-button-daily px-4" type="button" onClick={getSessionTypes}>
          Refresh
        </button>
      </div>

      {state.message && (
        <div
          className={`alert ${
            state.status === "error" ? "alert-danger" : "alert-success"
          }`}
          role="alert"
        >
          {state.message}
        </div>
      )}

      <table className="table mt-3">
        <thead>
          <tr>
            <th scope="col">Session Type ID</th>
            <th scope="col">Name</th>
            <th scope="col">Program ID</th>
            <th scope="col">Start Week</th>
            <th scope="col">End Week</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sessionTypes.map((item) => {
            const row = rowEdits[item.sessionTypeId] || {};
            const origStart = item.start_week !== undefined && item.start_week !== null ? String(item.start_week) : "";
            const origEnd = item.end_week !== undefined && item.end_week !== null ? String(item.end_week) : "";
            const isDirty = (row.start_week || "") !== origStart || (row.end_week || "") !== origEnd;
            return (
              <tr key={item.sessionTypeId}>
                <td>{item.sessionTypeId}</td>
                <td>{item.name}</td>
                <td>{item.programId}</td>
                <td>
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    max="52"
                    value={row.start_week || ""}
                    onChange={(event) =>
                      handleWeekChange(
                        item.sessionTypeId,
                        "start_week",
                        event.target.value
                      )
                    }
                  />
                </td>
                <td>
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    max="52"
                    value={row.end_week || ""}
                    onChange={(event) =>
                      handleWeekChange(
                        item.sessionTypeId,
                        "end_week",
                        event.target.value
                      )
                    }
                  />
                </td>
                <td className="d-flex gap-2">
                  {(isDirty || row.saving) && (
                  <>
                  <button
                    className="lb-button-daily px-3"
                    type="button"
                    onClick={() => saveWeekFilter(item.sessionTypeId)}
                    disabled={Boolean(row.saving)}
                  >
                    {row.saving ? "Saving..." : "Save"}
                  </button>
                  <button
                    className="lb-button-faq"
                    type="button"
                    onClick={() => resetRow(item.sessionTypeId)}
                    disabled={Boolean(row.saving)}
                  >
                    Reset
                  </button>
                  </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

WeekLimits.propTypes = {
  appState: PropTypes.object.isRequired,
};

export default WeekLimits;
